import React, { useState, useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextArea from '../../../components/FormFields/TextArea';

import useApi from '../../../hooks/useApi';
import lambdaBaseApi from '../../../apis/lambda';

import BobConfirmButton from './../../../components/BobConfirmButton';

import template from 'url-template';

import * as ArchitectActions from '../../../actions/architectActions';
import * as ApplicationMessageActions from '../../../actions/applicationMessageActions';
import * as BobActions from '../../../actions/bobActions';
import * as ServiceManagementActions from '../../../actions/serviceManagementActions';
import * as SvcRequestActions from '../../../actions/svcRequestActions';
import * as SiteActions from '../../../actions/siteActions';
import * as UiActions from '../../../actions/uiActions';

import { connect } from 'react-redux';

import classes from '../CreateEntityModal.module.css';
import { isEmpty } from 'lodash';
import { TextField } from '@material-ui/core';
import { serviceTechnicians } from '../../../data/schedulerData';
import { statusList } from '../../../data/schedulerStatus';
import DatePicker from 'react-datepicker';

const IssueTypesUrl = template.parse('/issuetypes');

const ValidationSchema = Yup.object().shape({
    client:         Yup.string().required(),
    site:           Yup.string().required(),
    equipment:      Yup.string(),
    faults:         Yup.array(),
    priority:       Yup.string().required(),
    technician:     Yup.string(),
    issueTypes:     Yup.array().required(),
    status:         Yup.string(),
    summary:        Yup.string(),
    description:    Yup.string(),
    startDate:      Yup.string(),
    completionDate: Yup.string(),
    estimatedHours: Yup.string(),
    actualHours:    Yup.string(),
    clientContact:  Yup.string(),
    serviceCall:    Yup.string()
});

const DEFAULT_FORM_VALUES = {
    client:         '',
    site:           '',
    equipment:      '',
    faults:         [],
    serviceCall:    '',
    priority:       0,
    technician:     '',
    issueTypes:     [],
    status:         '',
    summary:        '',
    description:    '',
    startDate:      '',
    completionDate: '',
    estimatedHours: '',
    actualHours:    '',
    clientContact:  ''
};

const formattedPriorities = [
    {
        id:   2,
        name: 'High'
    },
    {
        id:   1,
        name: 'Medium'
    },
    {
        id:   0,
        name: 'Low'
    }
];

const ServiceCallModal = ({
    close,
    scheduler,
    clearErrors,
    companyInfo,
    createSvcRequest,
    currentUser,
    equipment,
    errors,
    entityRules,
    fetchClientSites,
    fetchEntityRules,
    isLoading,
    navClients,
    open,
    sites,
    seniorityLevels,
    specialties,
    title,
    targetNavClient,
    targetNavSite,
    targetNavEquipment,
    toggleLoading,
    fetchEquipmentsBySiteId,
    onClose,
    ...props
}) => {
    const [formattedIssueTypes, setFormattedIssueTypes] = useState([]);
    const [formatedFaults, setFormatedFaults] = useState([]);

    const [issueTypes, fetchIssueTypes] = useApi(() => ({
        url: IssueTypesUrl.expand()
    }), lambdaBaseApi);

    const [equipments, setEquipments] = useState([]);
    const [initialValues, setInitialValues] = useState({ ...DEFAULT_FORM_VALUES });

    useEffect(() => {
        const init = async () => {
            await fetchEntityRules(equipment.companyId, equipment.equipmentId);
        };

        if (equipment && equipment.clientId) init();
    }, [equipment]);

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {

            const formatIssueTypes = (issueTypes) => {
                return issueTypes.map(type => type.id);
            };

            let body = {
                clientId:       values.client.id,
                siteId:         values.site.id,
                equipment:      values.equipment.id,
                faults:         values.faults,
                serviceCall:    values.serviceCall,
                priority:       values.priority.name,
                technician:     values.technician,
                issueTypes:     formatIssueTypes(values.issueTypes),
                status:         values.status,
                summary:        values.summary,
                description:    values.description,
                startDate:      values.startDate,
                completionDate: values.completionDate,
                estimatedHours: values.estimatedHours,
                actualHours:    values.actualHours,
                clientContact:  values.clientContact
            };

            let isUpdated;

            if (!isLoading) {
                toggleLoading(true);
                isUpdated = await createSvcRequest(body);
            }

            if (isUpdated) {
                handleClose();
            } else {
                toggleLoading(false);
            }
        }
    });

    useEffect(() => {
        if (!isEmpty(entityRules)) {
            const payload = entityRules.map(rule => {
                return { id: rule.ruleId, name: rule.ruleName };
            });

            const formatted = payload;

            setFormatedFaults(formatted);
        }
    }, [entityRules]);

    useEffect(() => {
        const init = async () => {
            await fetchIssueTypes();
            let clientId = '';
            let siteId = '';

            // targetNavClient object has values when they create service request from faults tab
            if (targetNavClient.id) {
                await fetchClientSites(targetNavClient.id);
                clientId = targetNavClient.id;
                siteId = targetNavSite.id;
            }

            // equipment object has values when they create service request from equipment tab
            if (equipment && equipment.clientId) {
                await fetchClientSites(equipment.clientId);
                clientId = equipment.clientId;
                siteId = equipment.siteId;
            }

            // defaulting entity values
            setInitialValues({
                ...initialValues,
                client: clientId,
                site:   siteId
            });
        };

        if (targetNavEquipment.id || (equipment && equipment.clientId)) init();
    }, [targetNavEquipment, equipment]);

    useEffect(() => {
        if (issueTypes.data) {
            const payload = issueTypes.data.map(issueType => {
                issueType.name = issueType.label;
                if (issueType.name === 'healthAndSafety') issueType.name = 'Health and Safety';

                return { id: issueType.id, name: issueType.name };
            });

            const formatted = payload;

            setFormattedIssueTypes(formatted);
        }
    }, [issueTypes]);

    const handleClientChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('site', '', false);

        toggleLoading(false);
        await fetchClientSites(value);

        formik.handleChange(event);
    };

    const handleSiteChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('equipment', '', false);

        formik.setTouched({
            equipment: false
        });

        const equipments = await fetchEquipmentsBySiteId(value);
        let equipmentList = [];
        if (equipments) {
            // eslint-disable-next-line array-callback-return
            equipments.map(item => {
                equipmentList.push(
                    {
                        id:   item.id,
                        name: item.name
                    }
                );
            });
        }
        setEquipments(equipmentList);
        toggleLoading(false);
        formik.handleChange(event);
    };

    const handleClose = () => {

    };

    return (
        <React.Fragment>
            <div className={classes.formColumn}>
                <div className={classes.createServiceCallInputColumn}>
                    <div className={classes.inputs}>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="client"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                                >
                                    Client<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.client}
                                    onChange={handleClientChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'client',
                                        id:     'client',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                                >
                                    {
                                        navClients.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.client &&
                                    formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="site"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                                >
                                    Site<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.site}
                                    onChange={scheduler ? handleSiteChange : formik.handleChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'site',
                                        id:     'site',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                                >
                                    {
                                        sites.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.site &&
                                    formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="equipment"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.equipment) && Boolean(formik.touched.equipment)}
                                >
                                    Equipments
                                </InputLabel>
                                <Select
                                    value={formik.values.equipment}
                                    onChange={formik.handleChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'equipment',
                                        id:     'equipment',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.site) && Boolean(formik.touched.equipment)}
                                >
                                    {
                                        equipments.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.equipment &&
                                    formik.errors.equipment && <FormHelperText className={classes.error}>{formik.errors.equipment}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="faults"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                >
                                    Faults
                                </InputLabel>
                                <Select
                                    multiple
                                    value={formik.values.faults}
                                    id="faults"
                                    input={<Input id="select-multiple-checkbox" />}
                                    renderValue={selected => selected.join(', ')}
                                    onChange={formik.handleChange}
                                    name="faults"
                                    className={`${classes.cssUnderline} ${classes.input}`}
                                    inputProps={{
                                        name: 'faults',
                                        id:   'faults',
                                    }}
                                >
                                    {
                                        formatedFaults.map(f => f.name).map((type, index) => (
                                            <MenuItem
                                                className={classes.selectMenuItem}
                                                value={type}
                                                key={index}
                                            >
                                                <Checkbox
                                                    color="primary"
                                                    checked={formik.values.faults.indexOf(type) > -1}
                                                />
                                                <span>{type}</span>
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <TextField
                                    className={`${classes.cssUnderline} ${classes.input}`}
                                    id="filled-required"
                                    label="Service Call"
                                    value={formik.values.serviceCall}
                                />
                                {formik.touched.issueType &&
                                    formik.errors.issueType && <FormHelperText className={classes.error}>{formik.errors.issueType}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="priority"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.priority) && Boolean(formik.touched.priority)}
                                >   
                                    Priority<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.priority}
                                    onChange={e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'priority',
                                        id:     'priority',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.priority) && Boolean(formik.touched.priority)}
                                >
                                    {
                                        formattedPriorities.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.priority &&
                                    formik.errors.priority && <FormHelperText className={classes.error}>{formik.errors.priority}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="technician"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.technician) && Boolean(formik.touched.technician)}
                                >   
                                    Technician
                                </InputLabel>
                                <Select
                                    value={formik.values.technician}
                                    onChange={e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'technician',
                                        id:     'technician',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.technician) && Boolean(formik.touched.technician)}
                                >
                                    {
                                        serviceTechnicians.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.employeeName}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.priority &&
                                    formik.errors.priority && <FormHelperText className={classes.error}>{formik.errors.priority}</FormHelperText>}
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="issueType"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.issueType) && Boolean(formik.touched.issueType)}
                                >
                                    Issue Type
                                </InputLabel>
                                <Select
                                    value={formik.values.issueType}
                                    onChange={e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'issueType',
                                        id:     'issueType',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.issueType) && Boolean(formik.touched.issueType)}
                                >
                                    {
                                        formattedIssueTypes.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.issueType &&
                                    formik.errors.issueType && <FormHelperText className={classes.error}>{formik.errors.issueType}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="status"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.status) && Boolean(formik.touched.status)}
                                >   
                                    Status<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.status}
                                    onChange={e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'status',
                                        id:     'status',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.status) && Boolean(formik.touched.status)}
                                >
                                    {
                                        statusList.map(type =>
                                            <MenuItem
                                                key={type.status}
                                                value={type.status}
                                            >
                                                {type.status}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.status &&
                                    formik.errors.status && <FormHelperText className={classes.error}>{formik.errors.status}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <TextArea
                                    name="summary"
                                    type="text"
                                    multiline={true}
                                    value={formik.values.summary}
                                    label="Summary"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error: classes.cssErrorUnderline,
                                            root:  classes.summaryTextArea
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.summary) && Boolean(formik.touched.summary)}
                                    helperText={Boolean(formik.touched.summary) && formik.errors.summary}
                                />
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="startDate"
                                    style={{transform: 'translateY(0)'}}
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.startDate) && Boolean(formik.touched.startDate)}
                                >
                                    Start Date
                                </InputLabel>
                                <DatePicker
                                    className={classes.calenderView}
                                    selected={formik.values.startDate}
                                    onChange={date => formik.handleChange(date)}
                                    timeInputLabel="Time:"
                                    dateFormat="MM/dd/yyyy h:mm aa"
                                    showTimeInput
                                />
                                {formik.touched.startDate &&
                                    formik.errors.startDate && <FormHelperText className={classes.error}>{formik.errors.startDate}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="completionDate"
                                    style={{transform: 'translateY(0)'}}
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.completionDate) && Boolean(formik.touched.completionDate)}
                                >   
                                    Completion Date
                                </InputLabel>
                                <DatePicker
                                    className={classes.calenderView}
                                    selected={formik.values.completionDate}
                                    onChange={date => formik.handleChange(date)}
                                    timeInputLabel="Time:"
                                    dateFormat="MM/dd/yyyy h:mm aa"
                                    showTimeInput
                                />
                                {formik.touched.completionDate &&
                                    formik.errors.completionDate && <FormHelperText className={classes.error}>{formik.errors.completionDate}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper} />
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="estimatedHours"
                                    style={{transform: 'translateY(0)'}}
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.estimatedHours) && Boolean(formik.touched.estimatedHours)}
                                >
                                    Estimated Hours
                                </InputLabel>
                                <DatePicker
                                    className={classes.calenderView}
                                    selected={formik.values.actualHours}
                                    onChange={date => formik.handleChange(date)}
                                    showTimeSelect
                                    showTimeSelectOnly
                                />
                                {formik.touched.estimatedHours &&
                                    formik.errors.estimatedHours && <FormHelperText className={classes.error}>{formik.errors.estimatedHours}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="actualHours"
                                    style={{transform: 'translateY(0)'}}
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.actualHours) && Boolean(formik.touched.actualHours)}
                                >   
                                    Actual Hours
                                </InputLabel>
                                {/* Todo: requires a change here */}
                                <DatePicker
                                    className={classes.calenderView}
                                    selected={formik.values.actualHours}
                                    onChange={date => formik.handleChange(date)}
                                    showTimeSelect
                                    showTimeSelectOnly
                                />
                                {formik.touched.actualHours &&
                                    formik.errors.actualHours && <FormHelperText className={classes.error}>{formik.errors.actualHours}</FormHelperText>}
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <TextArea
                                    name="description"
                                    type="text"
                                    multiline={true}
                                    value={formik.values.description}
                                    label="Description"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error: classes.cssErrorUnderline,
                                            root:  classes.summaryTextArea
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.summary) && Boolean(formik.touched.summary)}
                                    helperText={Boolean(formik.touched.summary) && formik.errors.summary}
                                />
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapperForCreateServiceCall}>
                                <InputLabel
                                    htmlFor="clientContact"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.clientContact) && Boolean(formik.touched.clientContact)}
                                >   
                                    Client Contact
                                </InputLabel>
                                <Select
                                    value={formik.values.clientContact}
                                    onChange={e => {
                                        formik.handleChange(e);
                                    }}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'clientContact',
                                        id:     'clientContact',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.clientContact) && Boolean(formik.touched.clientContact)}
                                >
                                    {
                                        statusList.map(type =>
                                            <MenuItem
                                                key={type.status}
                                                value={type.status}
                                            >
                                                {type.status}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.clientContact &&
                                    formik.errors.clientContact && <FormHelperText className={classes.error}>{formik.errors.clientContact}</FormHelperText>}
                            </FormControl>
                        </div>
                    </div>
                </div>
            </div>
            <React.Fragment>
                <Button
                    className={classes.Button}
                    onClick={onClose}
                    variant="contained"
                >
                    Cancel
                </Button>
                <BobConfirmButton
                    className={classes.ButtonWithMargin}
                    // disabled={formik.isSubmitting || isLoading}
                    isLoading={isLoading}
                    label={'Create'}
                // onClick={formik.handleSubmit}
                />
            </React.Fragment>
        </React.Fragment>
    );
};

ServiceCallModal.propTypes = {
    close:       func,
    companyInfo: object,
    currentUser: object,
    errors:      array,
    isLoading:   bool,
    open:        bool,
    rule:        object,
    title:       string,
    scheduler:   bool
};

const mapStateToProps = state => {
    return {
        companyInfo:        state.companyInfo,
        currentUser:        state.currentUser,
        errors:             state.messages.errors,
        entityRules:        state.bob.entityRules,
        isLoading:          state.ui.isLoading,
        navClients:         state.bob.navClients,
        sites:              state.site.sites,
        targetNavClient:    state.bob.targetNavClient,
        targetNavSite:      state.bob.targetNavSite,
        targetNavEquipment: state.bob.targetNavEquipment,
        seniorityLevels:    state.architect.seniorityLevels,
        specialties:        state.architect.specialties
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...BobActions,
    ...SiteActions,
    ...ServiceManagementActions,
    ...SvcRequestActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ServiceCallModal);
